import { memo, type ReactNode, useMemo } from 'react';
import { useAppDependencyGraphProvider } from '../../../context/DependencyGraphProvider';
import PageFunctionHelper from '../../../helper/PageFunctionHelper';
import { useNonReactiveAppComputeContext } from '../../../hooks/computeContext';
import { useGlobalIsEntitySuspended } from '../../../hooks/useIsEntitySuspended';
import { useTriggerGlobalAutomaticDataSources } from '../../../hooks/useTriggerAutomaticDataSources';
import { useGlobalAppStateStore } from '../../../stores/app/GlobalAppStateStore';
import { useInterfaceAppStore } from '../../../stores/InterfaceStore';
import type { PageFunctionControllerContextType } from '../context/PageFunctionControllerContext';
import { PageFunctionControllerProvider } from '../context/PageFunctionControllerProvider';
import { PageFunctionExecutor } from '../PageFunctionExecutor';

function AppPageFunctionControllerProvider({ children }: { children: ReactNode }) {
  const contextValue = useMemo<PageFunctionControllerContextType>(
    () => ({
      useInterfaceStore: useInterfaceAppStore,
      useNonReactiveComputeContext: useNonReactiveAppComputeContext,
      useGlobalStateStore: useGlobalAppStateStore,
      useDependencyGraphProvider: useAppDependencyGraphProvider,
      useTriggerAutomaticDataSources: useTriggerGlobalAutomaticDataSources,
      useIsEntitySuspended: useGlobalIsEntitySuspended,
    }),
    [],
  );

  return (
    <PageFunctionControllerProvider value={contextValue}>{children}</PageFunctionControllerProvider>
  );
}

function AppPageFunctionController() {
  const pageFunctions = useInterfaceAppStore().hooks.page.usePageFunctions();

  return (
    <AppPageFunctionControllerProvider>
      {Object.entries(pageFunctions || {}).map(([id, pageFunction]) => {
        if (!pageFunction) return null;
        // only mount automatic functions, manual functions are triggered via actions
        if (!PageFunctionHelper.isAutomatic(pageFunction)) {
          return null;
        }
        return <PageFunctionExecutor id={id} key={id} />;
      })}
    </AppPageFunctionControllerProvider>
  );
}

export default memo(AppPageFunctionController);
